
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { Calendar, User, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';

interface RecentMember {
  id: string;
  full_name: string;
  phone_number: string;
  email: string;
  state: string;
  local_government: string;
  created_at: string;
  approved: boolean;
  age_group?: string;
  gender?: string;
  sub_group?: string;
}

const RecentRegistrations = () => {
  const [recentMembers, setRecentMembers] = useState<RecentMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentRegistrations();
  }, []);

  const fetchRecentRegistrations = async () => {
    try {
      console.log('[RecentRegistrations] Fetching registrations from last 14 days...');
      
      // Calculate date 14 days ago
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      
      const { data, error } = await supabase
        .from('members')
        .select('*')
        .gte('created_at', fourteenDaysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[RecentRegistrations] Error fetching recent registrations:', error);
        throw error;
      }
      
      console.log('[RecentRegistrations] Successfully fetched recent registrations:', data?.length || 0);
      setRecentMembers(data || []);
    } catch (error) {
      console.error('[RecentRegistrations] Error:', error);
      toast.error('Failed to load recent registrations');
      setRecentMembers([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Recent Registrations (Last 14 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center">
            <div className="w-8 h-8 border-2 border-omoduduwa-indigo border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-gray-500">Loading recent registrations...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (recentMembers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Recent Registrations (Last 14 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center">
            <User className="mx-auto h-12 w-12 text-gray-300 mb-2" />
            <p className="text-gray-500">No registrations in the last 14 days</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Recent Registrations (Last 14 Days) - {recentMembers.length} Total
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Registered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      {member.full_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-3 h-3 text-gray-400" />
                        {member.phone_number}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Mail className="w-3 h-3 text-gray-400" />
                        {member.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="font-medium">{member.state}</div>
                        <div className="text-xs text-gray-500">{member.local_government}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={member.approved ? "default" : "secondary"}
                      className={member.approved ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}
                    >
                      {member.approved ? "Approved" : "Pending"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs space-y-1">
                      {member.age_group && <div>Age: {member.age_group}</div>}
                      {member.gender && <div>Gender: {member.gender}</div>}
                      {member.sub_group && <div>Group: {member.sub_group}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(member.created_at).toLocaleDateString()}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentRegistrations;
